/**
 * Document Uploader Component for Re:Nav-96
 *
 * A drag-and-drop document upload component with progress indicator.
 * Supports file selection via click or drag-drop.
 *
 * Features:
 * - Drag-and-drop file upload
 * - Click to select file
 * - Progress indicator during upload
 * - File type and size validation
 * - Optional description field
 * - Visual feedback for drag states
 * - Accessible with keyboard support
 *
 * @example
 * ```tsx
 * <DocumentUploader
 *   groupId="group-123"
 *   onUploadComplete={handleUpload}
 *   onUploadError={handleError}
 * />
 * ```
 */

import { useState, useRef, useCallback } from 'react';
import { documentsApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import './DocumentUploader.css';

/**
 * Upload state type
 */
export type UploadState = 'idle' | 'uploading' | 'success' | 'error';

/**
 * Uploaded document response
 */
interface UploadedDocument {
  id: string;
  filename: string;
  blobUrl: string;
}

/**
 * DocumentUploader component props
 */
interface DocumentUploaderProps {
  /** ID of the group to upload to */
  groupId: string;
  /** Callback when upload completes successfully */
  onUploadComplete?: (document: UploadedDocument) => void;
  /** Callback when upload fails */
  onUploadError?: (error: string) => void;
  /** Whether the component is disabled */
  disabled?: boolean;
  /** Maximum file size in bytes (default: 25MB) */
  maxFileSize?: number;
  /** Optional CSS class */
  className?: string;
}

/**
 * Allowed document types
 */
const ALLOWED_DOCUMENT_TYPES = [
  // Documents
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  // Text
  'text/plain',
  'text/csv',
  'text/markdown',
  // Images
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  // Archives
  'application/zip',
  'application/x-zip-compressed',
];

/**
 * Default max file size (25MB)
 */
const DEFAULT_MAX_FILE_SIZE = 25 * 1024 * 1024;

/**
 * Get human readable file types
 */
const FILE_TYPE_HINT = 'PDF, Word, Excel, PowerPoint, images, text, or ZIP files up to 25MB';

/**
 * DocumentUploader Component
 *
 * A reusable drag-and-drop document upload component.
 *
 * @param props - Component props
 * @returns The document uploader component
 */
export function DocumentUploader({
  groupId,
  onUploadComplete,
  onUploadError,
  disabled = false,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  className = '',
}: DocumentUploaderProps) {
  const { isAuthenticated } = useAuth();
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [isDragOver, setIsDragOver] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);

  /**
   * Validate the selected file
   */
  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
      return 'Invalid file type. Please upload a document, image, or ZIP file.';
    }

    // Check file size
    if (file.size > maxFileSize) {
      const maxSizeMB = Math.round(maxFileSize / (1024 * 1024));
      return `File size must be less than ${maxSizeMB}MB.`;
    }

    return null;
  }, [maxFileSize]);

  /**
   * Handle file selection from input or drop
   */
  const handleFile = useCallback((file: File) => {
    setValidationError(null);
    setUploadState('idle');

    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  }, [validateFile]);

  /**
   * Handle upload
   */
  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    try {
      setUploadState('uploading');
      setValidationError(null);

      const result = await documentsApi.upload(
        groupId,
        selectedFile,
        description.trim() || undefined
      );

      setUploadState('success');
      setSelectedFile(null);
      setDescription('');
      onUploadComplete?.(result);

      // Reset to idle after a delay
      setTimeout(() => {
        setUploadState('idle');
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload document';
      setUploadState('error');
      setValidationError(errorMessage);
      onUploadError?.(errorMessage);
    }
  }, [selectedFile, groupId, description, onUploadComplete, onUploadError]);

  /**
   * Handle click to open file dialog
   */
  const handleClick = useCallback(() => {
    if (!disabled && uploadState !== 'uploading') {
      fileInputRef.current?.click();
    }
  }, [disabled, uploadState]);

  /**
   * Handle keyboard activation
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && uploadState !== 'uploading') {
      e.preventDefault();
      fileInputRef.current?.click();
    }
  }, [disabled, uploadState]);

  /**
   * Handle file input change
   */
  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
    // Reset input to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFile]);

  /**
   * Handle drag enter
   */
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true);
    }
  }, []);

  /**
   * Handle drag leave
   */
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  }, []);

  /**
   * Handle drag over
   */
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  /**
   * Handle drop
   */
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    dragCounterRef.current = 0;

    if (disabled || uploadState === 'uploading') {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  }, [disabled, uploadState, handleFile]);

  /**
   * Clear selected file
   */
  const handleClear = useCallback(() => {
    setSelectedFile(null);
    setDescription('');
    setValidationError(null);
    setUploadState('idle');
  }, []);

  // Not authenticated - show login prompt
  if (!isAuthenticated) {
    return (
      <div className={`document-uploader-login-prompt ${className}`}>
        <svg
          className="w-5 h-5 text-white/40 mr-2"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span className="text-white/50 text-sm">Please log in to upload documents.</span>
      </div>
    );
  }

  const displayError = validationError;
  const isUploading = uploadState === 'uploading';
  const isSuccess = uploadState === 'success' && !displayError;
  const isError = uploadState === 'error' || !!displayError;

  // Build class names for dropzone
  const dropzoneClasses = [
    'document-uploader-dropzone',
    isDragOver && 'drag-over',
    isUploading && 'uploading',
    isSuccess && 'success',
    isError && 'error',
    disabled && 'disabled',
    selectedFile && 'has-file',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`document-uploader ${className}`}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_DOCUMENT_TYPES.join(',')}
        onChange={handleFileChange}
        className="document-uploader-input"
        aria-label="Select document to upload"
        disabled={disabled || isUploading}
      />

      {selectedFile ? (
        // Show selected file with description and upload button
        <div className="document-uploader-preview">
          <div className="document-uploader-file-info">
            <svg className="document-uploader-file-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <div className="document-uploader-file-details">
              <span className="document-uploader-filename">{selectedFile.name}</span>
              <span className="document-uploader-filesize">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </span>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="document-uploader-clear-btn"
              aria-label="Clear selected file"
              disabled={isUploading}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Description input */}
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add description (optional)"
            className="document-uploader-description"
            disabled={isUploading}
            maxLength={200}
          />

          {/* Error display */}
          {displayError && (
            <div className="document-uploader-error">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{displayError}</span>
            </div>
          )}

          {/* Upload button */}
          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading}
            className="document-uploader-upload-btn"
          >
            {isUploading ? (
              <>
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                  />
                </svg>
                Uploading...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                Upload Document
              </>
            )}
          </button>
        </div>
      ) : (
        // Show drop zone
        <>
          <div
            className={dropzoneClasses}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            role="button"
            tabIndex={disabled || isUploading ? -1 : 0}
            aria-label="Upload document. Click to select or drag and drop a file."
            aria-disabled={disabled || isUploading}
          >
            <div className="document-uploader-icon">
              {isSuccess ? (
                <svg className="document-uploader-success-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : isError ? (
                <svg className="document-uploader-error-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ) : (
                <svg className="document-uploader-upload-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              )}
            </div>

            <div className="document-uploader-text">
              {isSuccess ? (
                <>
                  <span className="document-uploader-title">Upload successful!</span>
                  <span className="document-uploader-subtitle">Document shared with the group.</span>
                </>
              ) : isError ? (
                <>
                  <span className="document-uploader-title">Upload failed</span>
                  <span className="document-uploader-subtitle document-uploader-error-text">
                    {displayError}
                  </span>
                </>
              ) : isDragOver ? (
                <span className="document-uploader-title">Drop your file here</span>
              ) : (
                <>
                  <span className="document-uploader-title">
                    Drag and drop your file here
                  </span>
                  <span className="document-uploader-subtitle">
                    or click to select
                  </span>
                  <span className="document-uploader-hint">
                    {FILE_TYPE_HINT}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Compact button for mobile */}
          <button
            type="button"
            className="document-uploader-button"
            onClick={handleClick}
            disabled={disabled || isUploading}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            Upload Document
          </button>
        </>
      )}
    </div>
  );
}

export default DocumentUploader;
